import { environmentHttpClient } from "client/Api/httpClient";
import {
	DocumentsViewModel,
	ExtendedInfo,
	LoadedExtendedInfo,
	ListLocationModel,
	ViewAndDocumentItemModel,
	FolderViewModel,
	RootItemsPageRequest,
	GridFolderContentsModel,
	FolderItemsPageRequest,
	DocumentItemViewModel,
	RecentDocumentItemViewModel,
	DocumentTemplateModel
} from "./common";

export function getAllDocumentTemplates(): Promise<DocumentTemplateModel[]> {
	return environmentHttpClient.get<DocumentTemplateModel[]>(
		"Documents/SharePoint/GetAllDocumentTemplates"
	);
}

export function getListDocumentTemplates(
	location: ListLocationModel,
	signal?: AbortSignal
): Promise<DocumentTemplateModel[]> {
	return environmentHttpClient.post<DocumentTemplateModel[]>(
		"Documents/SharePoint/GetListDocumentTemplates",
		location,
		{
			signal
		}
	);
}

export function getBreadCrumbsContentByFolder(
	folderId: number,
	location: ListLocationModel,
	signal?: AbortSignal
): Promise<FolderViewModel[]> {
	return environmentHttpClient.post<FolderViewModel[]>(
		"Documents/SharePoint/GetBreadCrumbsContentByFolder",
		{
			id: folderId,
			location: location,
			stopFolderId: null
		},
		{ signal }
	);
}

export type PendingSiteContentViewModel = { isLoaded: false; };

export type LoadedSiteContentViewModel = {
	isLoaded: true;
	hasError: boolean;
	views: readonly DocumentsViewModel[];
	subContainers: readonly DocumentsViewContainerViewModel[];
};
export type SiteContentViewModel = PendingSiteContentViewModel | LoadedSiteContentViewModel;

export type DocumentsViewContainerViewModel = {
	id: string;
	title: string;
	url: string;
	isPersonal: boolean;
	content: SiteContentViewModel;
	extendedInfo: ExtendedInfo<DocumentsViewContainerViewModelExtendedInfo>;
};

export type DocumentsViewsStructureModel = {
	structure: DocumentsViewContainerViewModel[];
	selectedViewId: string;
};

export type DocumentsViewContainerViewModelExtendedInfo = {
	canCreateSubsite: boolean;
	canManagePermissions: boolean;
};

export function getDocumentsViewStructure(
	hidePersonalViews: boolean,
	signal?: AbortSignal
): Promise<DocumentsViewsStructureModel> {
	return environmentHttpClient.get<DocumentsViewsStructureModel>(
		"Documents/SharePoint/GetDocumentsViewStructure",
		{
			hidePersonalViews
		},
		{ signal }
	);
}

export function getSiteContent(
	siteUrl: string,
	signal?: AbortSignal
): Promise<LoadedSiteContentViewModel> {
	return environmentHttpClient.get<LoadedSiteContentViewModel>(
		"Documents/SharePoint/GetSiteContent",
		{ siteUrl },
		{ signal }
	);
}

type GetExtendedInformationForHierarchyNodesRequestModel = {
	siteUrls: readonly string[];
};

export type GetExtendedInformationForHierarchyNodesResponseModel = {
	containers: { [containerId: string]: LoadedExtendedInfo<DocumentsViewContainerViewModelExtendedInfo>; };
};

export function getExtendedInformationForHierarchyNodes(
	siteUrls: string[]
): Promise<GetExtendedInformationForHierarchyNodesResponseModel> {
	const model: GetExtendedInformationForHierarchyNodesRequestModel = {
		siteUrls
	};

	return environmentHttpClient.post<GetExtendedInformationForHierarchyNodesResponseModel>(
		"Documents/SharePoint/GetExtendedInformationForHierarchyNodes",
		model
	);
}

export function getViewAndDocumentItemModelForLocation(
	location: ListLocationModel,
	itemId?: number | nil,
	signal?: AbortSignal
): Promise<ViewAndDocumentItemModel> {
	return environmentHttpClient.get<ViewAndDocumentItemModel>(
		"Documents/SharePoint/GetViewAndDocumentItemModelForLocation",
		{ location, itemId },
		{signal}
	);
}

export function listIllegalCharacters(): Promise<string[]> {
	return environmentHttpClient.get<string[]>(
		"Documents/SharePoint/ListIllegalCharacters"
	);
}

export function getRootContent(
	request: RootItemsPageRequest,
	signal?: AbortSignal
): Promise<GridFolderContentsModel> {
	return environmentHttpClient.get<GridFolderContentsModel>(
		"Documents/SharePoint/GetRootContent",
		request,
		{ signal }
	);
}

export function getRecentDocuments(
	signal?: AbortSignal
): Promise<readonly RecentDocumentItemViewModel[]> {
	return environmentHttpClient.get<readonly RecentDocumentItemViewModel[]>(
		"Documents/SharePoint/ListRecentDocuments",
		undefined,
		{
			signal
		}
	);
}

export function getFolderContent(
	request: FolderItemsPageRequest,
	signal?: AbortSignal
): Promise<GridFolderContentsModel> {
	return environmentHttpClient.get<GridFolderContentsModel>(
		"Documents/SharePoint/GetFolderContent",
		request,
		{
			signal
		}
	);
}

export function clearDmsCache(
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"Documents/SharePoint/ClearCache",
		undefined,
		{signal}
	);
}

export function itemLocationUrl(
	itemId: number,
	location: ListLocationModel,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.get<string>(
		"/Documents/SharePoint/ItemLocationUrl",
		{ itemId, location },
		{ signal }
	);
};

export function getDocumentInformation(
	location: ListLocationModel,
	itemId: number,
	signal?: AbortSignal
): Promise<DocumentItemViewModel> {
	return environmentHttpClient.get<DocumentItemViewModel>(
		"Documents/Sharepoint/GetDocumentInformation",
		{
			location,
			itemId
		},
		{ signal }
	);
};